import { faFlag } from "@fortawesome/pro-solid-svg-icons"
import {
  Autocomplete,
  Box,
  List,
  ListItem,
  ListItemText,
  Popper,
  styled,
  TextField,
  Typography
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import { useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useRef } from "react"
import * as Yup from "yup"
import { StyledBoxFullWidth, StyledListItemText } from "./customFieldGroup.styled"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { StyledListItem } from "shared/styled/General"
import { t } from "i18next"

const validationSchema = Yup.object().shape({
  name: Yup.string().required(t("global.name_is_required", "Name is required")),
  customFieldNames: Yup.array()
    .min(1, t("vehicleregistry.at_least_one_field_must_be_selected", "At least one field must be selected"))
    .required(
      t("vehicleregistry.at_least_one_field_must_be_selected", "At least one field must be selected")
    ),
  vehiclesInfo: Yup.array()
    .min(
      1,
      t("vehicleregistry.at_least_one_vehicle_must_be_selected", "At least one vehicle must be selected")
    )
    .required(
      t("vehicleregistry.at_least_one_vehicle_must_be_selected", "At least one vehicle must be selected")
    )
})

const NameTextField = () => {
  const { errors, handleBlur, handleChange, touched, values } = useFormikContext()

  return (
    <StyledListItem>
      <StyledBoxFullWidth>
        <StyledListItemText primary={t("global.name", "Name")} style={{ paddingBottom: "8px" }} />
      </StyledBoxFullWidth>
      <TextField
        autoComplete="off"
        fullWidth
        id="name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.name && errors.name ? true : false}
        helperText={touched.name && errors.name ? t("global.name_is_required", "Name is required") : null}
        variant="outlined"
        InputProps={{
          disableUnderline: true
        }}
      />
    </StyledListItem>
  )
}

const CustomFieldMultiselect = () => {
  const { errors, handleBlur, handleChange, setFieldValue, touched, values } = useFormikContext()
  const [customFieldsResult, getCustomFields] = useApi()
  const customFieldNames = customFieldsResult.data

  useEffect(() => {
    getCustomFields.get(apiEndpoints.vehicleregistryadmin.listactivecustomfieldnames)
  }, [])

  return (
    <StyledListItem>
      <StyledBoxFullWidth>
        <StyledListItemText primary={t("global.field", "Field")} style={{ paddingBottom: "8px" }} />
      </StyledBoxFullWidth>
      <Autocomplete
        sx={{ width: "100%" }}
        multiple
        id="customFieldNames"
        options={customFieldNames || []}
        getOptionLabel={(option) => option.name}
        value={values.customFieldNames || []}
        getOptionSelected={(option, value) => {
          return option.name === value.name
        }}
        onChange={(e, value) => {
          setFieldValue("customFieldNames", value)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={("customFieldNames", "")}
            name="customFields"
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.customFieldNames && errors.customFieldNames ? true : false}
            helperText={
              touched.customFieldNames && errors.customFieldNames
                ? t("global.field_is_required", "Field is required")
                : null
            }
          />
        )}
      />
    </StyledListItem>
  )
}

const StyledPopper = styled(Popper)({
  "& .MuiAutocomplete-paper": {
    maxHeight: "300px",
    overflowY: "auto"
  }
})

const VehicleMultiSelect = () => {
  const { errors, handleBlur, handleChange, setFieldValue, touched, values } = useFormikContext()
  const [vehiclesResult, vehiclesApi] = useApi()

  useEffect(() => {
    vehiclesApi.get(apiEndpoints.vehicleregistry.getvehiclelist)
  }, [])

  return (
    <StyledListItem>
      <StyledBoxFullWidth>
        <StyledListItemText primary={t("global.vehicle", "Vehicle")} style={{ paddingBottom: "8px" }} />
      </StyledBoxFullWidth>
      <Autocomplete
        sx={{
          width: "100%",
          maxHeight: "160px",
          overflowY: "auto"
        }}
        multiple
        id="vehiclesInfo"
        filterSelectedOptions
        options={vehiclesResult.data || []}
        getOptionLabel={(option) => option.regNumber + " / " + option.internnumber}
        value={values.vehiclesInfo || []}
        isOptionEqualToValue={(option, value) => {
          return option.regNumber === value.regNumber
        }}
        noOptionsText={t("global.no_vehicles_available", "No vehicles available")}
        onChange={(e, value) => {
          setFieldValue("vehiclesInfo", value)
        }}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={("vehiclesInfo", "")}
            name="vehicleInfo"
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.vehiclesInfo && errors.vehiclesInfo ? true : false}
            helperText={
              touched.vehiclesInfo && errors.vehiclesInfo
                ? t("global.vehicle_is_required", "Vehicles are required")
                : null
            }
          />
        )}
      />
      <Button
        color={values.vehiclesInfo.length > 0 ? "error" : "primary"}
        sx={{ marginTop: "20px", marginX: "auto" }}
        onClick={() => {
          if (values.vehiclesInfo.length > 0) {
            setFieldValue("vehiclesInfo", [])
            return
          }
          const allVehicles = vehiclesResult.data.map((vehicle) => ({
            id: vehicle.id,
            regNumber: vehicle.regNumber,
            internnumber: vehicle.internnumber
          }))
          setFieldValue("vehiclesInfo", allVehicles)
        }}>
        {values.vehiclesInfo.length > 0
          ? t("vehicleregistry.remove_all_vehicles", "Remove all vehicles")
          : t("vehicleregistry.add_all_vehicles", "Add all vehicles")}
      </Button>
    </StyledListItem>
  )
}

const CustomFieldGroupForm = (props) => {
  const appContext = useContext(AppContext)

  const { editItemResult, addResult, updateResult, editMode, onAddSubmit, onUpdateSubmit } = props
  const [usersResult, getUsers] = useApi()
  useEffect(() => {
    getUsers.get(apiEndpoints.vehicleregistryadmin.getuserstoassign)
  }, [])
  const formRef = useRef()

  const initialValues =
    editMode && editItemResult.status === 2
      ? {
          ...editItemResult.data
        }
      : {
          id: 0,
          name: "",
          customFieldNames: [],
          vehiclesInfo: []
        }

  const handleSubmit = (formikValues) => {
    let filteredValues = { ...formikValues }

    if (editMode) {
      onUpdateSubmit(filteredValues)
    } else {
      onAddSubmit(filteredValues)
    }
  }

  useEffect(() => {
    if (!appContext.isSidebarOpen) {
      formRef?.current?.resetForm()
    }
  }, [appContext.isSidebarOpen])

  const buttonFeedbackResult = editMode ? updateResult : addResult

  return (
    <Sidebar
      initialFormikValues={initialValues}
      setInitialFormikValues={() => {}}
      formResult={buttonFeedbackResult}
      formData={{
        formId: "CustomFieldGroupForm",
        titleNew: t("vehicleregistry.add_group", "Add group"),
        titleEdit: t("global.edit_group", "Edit group") + initialValues?.name,
        subTitleEdit: t("global.edit_group", "Edit group"),
        submitEditText: t("global.save", "Save"),
        submitText: t("global.save", "Save"),
        icon: faFlag
      }}
      handleSubmit={handleSubmit}
      validationSchema={validationSchema}
      formRef={formRef}>
      <Box display="flex" flexDirection="column" gap={2}>
        <NameTextField />
        <CustomFieldMultiselect />
        <VehicleMultiSelect />
      </Box>
    </Sidebar>
  )
}

export default CustomFieldGroupForm
